import React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CoachCard } from "~/components/coach-card";
import Heading from "~/components/ui/heading";
import { getCityLabelFromValue } from "~/lib/functions/cities";
import { getCityPageContent } from "~/lib/functions/coach-group-seo";
import { BreakdownIntro } from "~/components/feature/breakdown-intro";
import { MidListCTA } from "~/components/feature/mid-list-cta";
import { BottomCTA } from "~/components/feature/bottom-cta";
import { coachQueryOptions } from "~/lib/query-options";
import { getCategoriesInCityWithCoaches } from "~/lib/functions/expertise";
import Subheading from "~/components/ui/subheading";
import { ScrollingTagList } from "~/components/scrolling-tag-list";
import { MarkdownContent } from "~/components/markdown-content";
import { FaqList } from "~/components/faq-list";
import { CTASection } from "~/components/ui/cta-section";
import {
  createBrandMeta,
  createLocalBusinessStructuredData,
  createServiceStructuredData,
} from "~/lib/shared/meta";

// TODO: add cacheControl middleware to TanStack Start's global middleware

export const Route = createFileRoute("/_public/coaches/city/$slug/")({
  loader: async ({ context, params }) => {
    const citySlug = params.slug;

    // Pre-load data into React Query and also fetch for head function
    const [_coaches, _categories, page, cityLabel] = await Promise.all([
      context.queryClient.ensureQueryData(coachQueryOptions.byCity(citySlug)),
      context.queryClient.ensureQueryData({
        queryKey: ["categories", "city", citySlug],
        queryFn: () => getCategoriesInCityWithCoaches({ data: { citySlug } }),
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["cityPageContent", citySlug],
        queryFn: () => getCityPageContent({ data: { citySlug } }),
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["cityLabel", citySlug],
        queryFn: () => getCityLabelFromValue({ data: { citySlug } }),
      }),
    ]);

    return {
      page,
      cityLabel,
    };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      const metaConfig = createBrandMeta({
        title: "Professional Coaches | Dream Coach Match",
        description:
          "Find certified professional coaches in your area. Connect with life coaches, business coaches, and health coaches.",
        canonical: `https://dreamcoachmatch.com/coaches/city/${params.slug}`,
        noindex: false,
      });
      return metaConfig;
    }

    const { page, cityLabel } = loaderData;
    const name = cityLabel || "this city";
    const citySlug = params.slug;

    const title =
      page?.seoTitle ?? `Professional Coaches in ${name} | Dream Coach Match`;
    const description =
      page?.metaDescription ??
      `Dream Coach Match makes it easy to find certified life coaches, health coaches, business and executive coaches in ${name} — helping you find the right fit. Many offer virtual sessions or in-person coaching in ${name}.`;

    const metaConfig = createBrandMeta({
      title,
      description,
      ogTitle: title,
      ogDescription: description,
      ogImage: `https://dreamcoachmatch.com/img/og/city-${citySlug}.jpg`,
      ogType: "website",
      ogUrl: `https://dreamcoachmatch.com/coaches/city/${citySlug}`,
      twitterCard: "summary_large_image",
      twitterTitle: `Find Professional Coaches in ${name} | Dream Coach Match`,
      twitterDescription: `Discover certified life coaches, business coaches, and health coaches in ${name}. Connect with the right coach for your personal and professional development goals.`,
      twitterImage: `https://dreamcoachmatch.com/img/og/city-${citySlug}.jpg`,
      canonical: `https://dreamcoachmatch.com/coaches/city/${citySlug}`,
      keywords: [
        "coaches",
        name,
        "life coach",
        "business coach",
        "health coach",
        "executive coach",
        "professional coaching",
        `coaches in ${name}`,
        `${name} coaching`,
        "local coaches",
        "find coach",
      ],
      author: "Dream Coach Match",
    });

    const serviceStructuredData = createServiceStructuredData({
      name: `Professional Coaching Services in ${name}`,
      description: `Find certified professional coaches in ${name}. Life coaches, business coaches, health coaches, and executive coaches available for in-person and virtual sessions.`,
      provider: "Dream Coach Match",
      url: `https://dreamcoachmatch.com/coaches/city/${citySlug}`,
      image: "https://dreamcoachmatch.com/img/logo/dcm-horizontal-1.png",
    });

    // Extract city and state from name if possible (format: "City, State")
    const [city, state] = name.includes(",")
      ? name.split(",").map((s) => s.trim())
      : [name, ""];

    const localBusinessStructuredData = createLocalBusinessStructuredData({
      name: `Dream Coach Match - ${name}`,
      description: `Professional coaching services directory for ${name}. Connect with certified life coaches, business coaches, health coaches, and executive coaches in your area.`,
      address: {
        addressLocality: city,
        addressRegion: state || city,
        addressCountry: "US",
      },
      url: `https://dreamcoachmatch.com/coaches/city/${citySlug}`,
      image: `https://dreamcoachmatch.com/img/og/city-${citySlug}.jpg`,
      priceRange: "$50-$500",
      serviceArea: [name],
    });

    const collectionPageStructuredData = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `Professional Coaches in ${name}`,
      description: description,
      url: `https://dreamcoachmatch.com/coaches/city/${citySlug}`,
      mainEntity: {
        "@type": "ItemList",
        name: `Coaches in ${name}`,
        description: `Directory of professional coaches available in ${name}`,
        itemListOrder: "Unordered",
      },
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://dreamcoachmatch.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Coaches",
            item: "https://dreamcoachmatch.com/coaches",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: name,
            item: `https://dreamcoachmatch.com/coaches/city/${citySlug}`,
          },
        ],
      },
    };

    return {
      ...metaConfig,
      scripts: [
        {
          key: "jsonld-service",
          props: {
            type: "application/ld+json",
            dangerouslySetInnerHTML: JSON.stringify(serviceStructuredData),
          },
        },
        {
          key: "jsonld-localbusiness",
          props: {
            type: "application/ld+json",
            dangerouslySetInnerHTML: JSON.stringify(
              localBusinessStructuredData,
            ),
          },
        },
        {
          key: "jsonld-collectionpage",
          props: {
            type: "application/ld+json",
            dangerouslySetInnerHTML: JSON.stringify(
              collectionPageStructuredData,
            ),
          },
        },
      ],
    };
  },
  component: CityCoachesPage,
});

function CityCoachesPage() {
  const { slug: citySlug } = Route.useParams();

  const { data: coaches = [] } = useQuery(coachQueryOptions.byCity(citySlug));

  const { data: activeCategories = [] } = useQuery({
    queryKey: ["categories", "city", citySlug],
    queryFn: () => getCategoriesInCityWithCoaches({ data: { citySlug } }),
  });

  const { data: page } = useQuery({
    queryKey: ["cityPageContent", citySlug],
    queryFn: () => getCityPageContent({ data: { citySlug } }),
  });

  const { data: cityLabel } = useQuery({
    queryKey: ["cityLabel", citySlug],
    queryFn: () => getCityLabelFromValue({ data: { citySlug } }),
  });

  if (!coaches) {
    // TODO: handle 404 case properly - throw notFound() in loader instead
    return <div>Coaches for this city not found</div>;
  }

  const name = cityLabel || citySlug;
  // Prefer SEO content from page, fallback to current text
  const headline =
    page?.seoTitle || `Looking for a professional coach in ${name}?`;
  const subheadline =
    page?.metaDescription ||
    `Dream Coach Match makes it easy to find certified life coaches, health coaches, business and executive coaches in ${name} — whether you're seeking clarity, growth, or support during a major transition.`;
  const intro1 = `Each coach profile includes key details about their background, coaching style, and areas of focus — helping you find the right fit for your goals. Many of the coaches featured on Dream Coach Match offer virtual sessions, and several are available for in-person coaching right here in ${name}.`;
  const intro2 = `Browse our growing list of coaches in ${name} and discover the perfect match for your goals, challenges, and aspirations.`;

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <Heading headline={headline} subheadline={subheadline} />
      <BreakdownIntro
        breakdownName={name}
        breakdownTypeSlug={citySlug}
        introTextLine1={intro1}
        introTextLine2={intro2}
        ctaButtonTextTemplate="Use our Matchmaker to Find Your Dream Coach in {}"
        utmCampaignPrefix="city_coaches"
      />
      {activeCategories && 0 < activeCategories.length && (
        <div className="my-12">
          <Subheading className="text-center">
            {`What kind of coaching are you looking for in ${name}?`}
          </Subheading>
          <div className="mt-6">
            <ScrollingTagList
              items={activeCategories.map((cat) => ({
                id: cat.id,
                name: cat.name,
                listingName:
                  null === cat.listingName ? undefined : cat.listingName,
                slug: cat.slug,
              }))}
              baseHref={`/coaches/city/${citySlug}/category/`}
            />
          </div>
        </div>
      )}
      <div className="mt-12 grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
        {0 === coaches.length ? (
          <p className="col-span-full text-center text-lg text-gray-500">
            No coaches found for this city yet. Check back soon!
          </p>
        ) : (
          coaches.map((coach, i) => (
            <React.Fragment key={coach.id}>
              <CoachCard
                coachData={{
                  coach: {
                    id: coach.id,
                    username: coach.username,
                    fullName: coach.fullName,
                    membershipPlan: coach.membershipPlan,
                    profilePictureUrl: coach.profilePictureUrl,
                  },
                  profile: {
                    coachingTitle: coach.coachingTitle,
                    brandLogoUrl: coach.brandLogoUrl,
                    brandName: coach.brandName,
                    title: coach.title,
                  },
                }}
              />
              {11 === i && (
                <MidListCTA
                  breakdownName={name}
                  breakdownTypeSlug={citySlug}
                  preLinkText="Not sure which coach to choose?"
                  ctaLinkTextTemplate="Try Our Free Coach Matching Tool for {}"
                  utmCampaignPrefix="city_coaches_mid"
                />
              )}
            </React.Fragment>
          ))
        )}
      </div>
      <BottomCTA
        itemCount={coaches.length}
        itemTypePlural="coaches"
        breakdownName={name}
        breakdownTypeSlug={citySlug}
        joinAsLinkUrl="/page/coach"
        joinAsLinkText="Join as a coach."
        mainCtaSubTextTemplate={`Ready to connect with your dream coach in ${name}?`}
        mainCtaButtonTextTemplate="Find Your Dream Coach"
        utmCampaignPrefix="city_coaches_bottom"
      />
      {page?.contentMd && (
        <div className="prose mx-auto mt-12">
          <MarkdownContent content={page.contentMd} />
        </div>
      )}
      <div className="mx-auto mt-12 max-w-prose">
        <CTASection
          title={`Ready to connect with your dream coach in ${name}?`}
          linkText="Find Your Dream Coach"
          link={`/discovery?utm_campaign=city_coaches_bottom&utm_source=${citySlug}`}
        >
          <div className="mt-4">
            <a
              href="/page/member"
              className="text-brand-700 hover:text-brand-800 my-2 text-center text-xs underline transition-colors hover:no-underline"
            >
              How does Dream Coach Match work?
            </a>
          </div>
        </CTASection>
      </div>
      {page?.faqItems && 0 < page.faqItems.length && (
        <FaqList items={page.faqItems} className="mx-auto max-w-2xl" />
      )}
    </div>
  );
}

export default CityCoachesPage;
